import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MessageCircle, X, Send, ChevronRight, Sparkles,
  ArrowRight, Minimize2, Maximize2, Bot,
} from "lucide-react";

// ─────────────────────────────────────────────────
// KNOWLEDGE BASE — The brain of the chatbot
// No API calls. Everything is local.
// ─────────────────────────────────────────────────

const KNOWLEDGE = {
  // Page directory - what each page does and how to get there
  pages: {
    dashboard: {
      path: "dashboard",
      keywords: ["dashboard", "home", "overview", "main", "start"],
      description: "Your main hub showing key metrics, recent activity, tasks due, weather, and a summary of your pipeline.",
      tips: "The dashboard adapts to your CRM type — it shows different widgets for agents vs wholesalers vs flippers.",
    },
    clients: {
      path: "clients",
      keywords: ["clients", "contacts", "leads", "people", "customers", "client list", "add client", "add contact", "add lead"],
      description: "Manage all your contacts — leads, active clients, past clients, and referrals. Add, edit, tag, filter, and track every interaction.",
      tips: "Use the search bar to find clients fast. Click any client to see their full timeline. Import clients from CSV or Google Contacts.",
    },
    properties: {
      path: "properties",
      keywords: ["properties", "listings", "houses", "homes", "real estate", "property", "mls", "listing"],
      description: "View all your properties — active listings, sold, pending, and off-market. Each property shows Walk Score, schools, weather, and street view.",
      tips: "Click any property for full details including Walk Score, nearby schools, tax info, and map view.",
    },
    transactions: {
      path: "transactions",
      keywords: ["transactions", "deals", "closings", "escrow", "under contract"],
      description: "Track all your transactions from contract to close. See commissions, expenses, key dates, and linked documents.",
      tips: "Each transaction can have per-deal expenses tracked. Use the closing countdown to never miss a deadline.",
    },
    calendar: {
      path: "calendar",
      keywords: ["calendar", "schedule", "events", "appointments", "meetings", "showing"],
      description: "Your full calendar with Google Calendar sync. Schedule showings, meetings, follow-ups, and open houses.",
      tips: "Syncs with Google Calendar automatically. Color-coded by event type.",
    },
    tasks: {
      path: "tasks",
      keywords: ["tasks", "to do", "todo", "reminders", "action items"],
      description: "Manage your task list — due dates, priorities, categories, and completion tracking.",
      tips: "Tasks can be linked to clients or properties. Set recurring tasks for regular follow-ups.",
    },
    pipeline: {
      path: "pipeline",
      keywords: ["pipeline", "funnel", "stages", "deal flow", "sales pipeline"],
      description: "Visual pipeline showing all your deals moving through stages — from lead to close. Drag and drop to update.",
      tips: "Available on Power plan and above. Great for visualizing where all your deals stand at a glance.",
    },
    emailInbox: {
      path: "email-inbox",
      keywords: ["email", "inbox", "gmail", "mail", "messages", "email inbox"],
      description: "Full Gmail integration — read, send, and compose emails without leaving the CRM. All client emails in one place.",
      tips: "Connect your Gmail in Connected Accounts first. Emails are automatically linked to matching client records.",
    },
    emailTemplates: {
      path: "email-templates",
      keywords: ["email templates", "templates", "email drafts", "canned responses"],
      description: "Pre-built and custom email templates for follow-ups, listing updates, market reports, and more.",
      tips: "Saves hours of writing. Customize any template with merge fields like {client_name} and {property_address}.",
    },
    messaging: {
      path: "messaging",
      keywords: ["text", "sms", "messaging", "text message", "texting"],
      description: "Send and receive text messages directly from the CRM. Track all SMS conversations.",
      tips: "Set up your phone number first in Settings. All texts are logged to the client's communication history.",
    },
    basicDialer: {
      path: "basic-dialer",
      keywords: ["dialer", "phone", "call", "calling", "cold call", "power dialer"],
      description: "Built-in dialer for making calls. Log calls, take notes, and track outcomes — all from within the CRM.",
      tips: "Great for FSBO prospecting and sphere of influence calls. Call notes auto-save to the client record.",
    },
    communications: {
      path: "communications",
      keywords: ["communications", "communication log", "interaction history", "activity log"],
      description: "See all communications across every channel — emails, calls, texts, notes — in one unified timeline.",
      tips: "Filter by client, date range, or communication type to find any interaction quickly.",
    },
    documents: {
      path: "documents",
      keywords: ["documents", "files", "contracts", "uploads", "paperwork", "document"],
      description: "Store and organize all your documents — contracts, disclosures, inspection reports, and more.",
      tips: "Documents can be linked to specific clients, properties, or transactions for easy retrieval.",
    },
    marketingTools: {
      path: "marketing-tools",
      keywords: ["marketing", "flyers", "postcards", "social media", "marketing tools", "templates"],
      description: "64+ marketing tools including flyers, postcards, social media templates, email campaigns, door hangers, listing presentations, and more.",
      tips: "Everything from Just Sold postcards to Coming Soon door hangers. No design skills needed.",
    },
    dripCampaign: {
      path: "drip-campaign",
      keywords: ["drip", "drip campaign", "automation", "automated emails", "nurture", "sequence"],
      description: "Create automated email sequences that nurture leads over time. Set it and forget it.",
      tips: "Use pre-built campaigns for buyer leads, seller leads, past clients, and sphere of influence.",
    },
    autoDrip: {
      path: "auto-drip",
      keywords: ["auto drip", "automated drip", "smart campaigns"],
      description: "Advanced automated drip campaigns with smart triggers and conditional logic.",
      tips: "Power plan feature. Set up campaigns that trigger based on client behavior and status changes.",
    },
    showingScheduler: {
      path: "showing-scheduler",
      keywords: ["showing", "showing scheduler", "schedule showing", "showings", "house showing"],
      description: "Schedule and manage property showings. See weather forecasts for each showing time.",
      tips: "Weather integration shows conditions at the property for the showing time — rain warnings included!",
    },
    showingFeedback: {
      path: "showing-feedback",
      keywords: ["feedback", "showing feedback", "buyer feedback", "listing feedback"],
      description: "Collect and track feedback from buyers after showings. Know what they liked and didn't.",
      tips: "Send automated feedback request links after showings. Compile feedback reports for sellers.",
    },
    listingChecklist: {
      path: "listing-checklist",
      keywords: ["listing checklist", "checklist", "listing preparation", "pre-listing"],
      description: "Step-by-step checklist for preparing and managing listings from start to close.",
      tips: "Never miss a step — covers everything from pre-listing prep to final walkthrough.",
    },
    listingLifecycle: {
      path: "listing-lifecycle",
      keywords: ["listing lifecycle", "listing stages", "listing status", "listing tracker"],
      description: "Track each listing through its full lifecycle — pre-listing to closed. Visual timeline with milestones.",
      tips: "Status auto-syncs with the property record. See exactly where every listing stands.",
    },
    buyerPreferences: {
      path: "buyer-preferences",
      keywords: ["buyer preferences", "buyer search", "buyer wants", "buyer criteria", "buyer matching"],
      description: "Track what each buyer is looking for — price range, beds, baths, location, must-haves, deal-breakers.",
      tips: "Match buyers to properties automatically based on their saved preferences.",
    },
    commissionTracker: {
      path: "commission-tracker",
      keywords: ["commission", "commissions", "earnings", "income", "gross commission"],
      description: "Track all your commissions — pending, earned, and paid. See your income at a glance.",
      tips: "Links to transactions automatically. Track splits with your broker.",
    },
    closingCountdown: {
      path: "closing-countdown",
      keywords: ["closing", "closing countdown", "closing date", "days until close"],
      description: "Visual countdown timers for all your upcoming closings. Never miss a deadline.",
      tips: "Shows inspection, appraisal, financing, and closing deadlines for every deal.",
    },
    openHouseToolkit: {
      path: "open-house-toolkit",
      keywords: ["open house", "open houses", "sign in sheet", "visitor tracking"],
      description: "Complete open house management — digital sign-in, visitor tracking, and automated follow-up.",
      tips: "Visitors sign in on your tablet and automatically get added as leads with follow-up sequences.",
    },
    eSignature: {
      path: "e-signature",
      keywords: ["e-signature", "esign", "digital signature", "sign documents", "esignature"],
      description: "Send documents for electronic signature directly from the CRM.",
      tips: "Power plan feature. No need for DocuSign — it's built right in.",
    },
    leadCapture: {
      path: "lead-capture",
      keywords: ["lead capture", "capture leads", "lead form", "website leads"],
      description: "Create lead capture forms and landing pages to collect leads from your website.",
      tips: "Power plan feature. Embed forms on your website or share direct links.",
    },
    analytics: {
      path: "analytics",
      keywords: ["analytics", "reports", "statistics", "data", "metrics", "reporting"],
      description: "Comprehensive analytics — lead sources, conversion rates, revenue, activity metrics, and more.",
      tips: "See which lead sources convert best and where your time is most valuable.",
    },
    agentKpis: {
      path: "agent-kpis",
      keywords: ["kpi", "kpis", "performance", "agent performance", "agent kpi"],
      description: "Track your key performance indicators — calls made, appointments set, listings taken, deals closed.",
      tips: "Pro plan feature. Set targets and track progress week over week.",
    },
    profitLoss: {
      path: "profit-loss-tracker",
      keywords: ["profit", "loss", "p&l", "profit and loss", "expenses", "income vs expenses"],
      description: "Full profit and loss tracking — see your true net income after all business expenses.",
      tips: "Track marketing costs, MLS fees, gas, gifts, and every other expense.",
    },
    goalTracker: {
      path: "goal-tracker",
      keywords: ["goals", "goal tracker", "targets", "goal setting"],
      description: "Set and track business goals — transactions, GCI, new leads, and more.",
      tips: "Break annual goals into monthly and weekly targets. Stay accountable.",
    },
    neighborhoodFarms: {
      path: "neighborhood-farms",
      keywords: ["farming", "neighborhood farm", "geographic farm", "area farming"],
      description: "Manage geographic farming campaigns — target specific neighborhoods with direct mail and door knocking.",
      tips: "Pro plan feature. Track market share growth in your farm areas.",
    },
    soiTracker: {
      path: "soi-tracker",
      keywords: ["sphere", "soi", "sphere of influence", "referral", "personal network"],
      description: "Manage your sphere of influence — track touches, birthdays, anniversaries, and referral potential.",
      tips: "Pro plan feature. The #1 source of business for top agents. Don't neglect your sphere!",
    },
    followUpSequences: {
      path: "follow-up-sequences",
      keywords: ["follow up", "followup", "follow-up sequence", "nurture sequence"],
      description: "Create multi-step follow-up sequences combining emails, texts, calls, and tasks.",
      tips: "Pro plan feature. Set up once and let the system handle follow-ups for months.",
    },
    vendorScorecard: {
      path: "vendor-scorecard",
      keywords: ["vendor", "vendors", "contractor", "inspector", "lender", "title company", "vendor scorecard"],
      description: "Rate and track your preferred vendors — inspectors, lenders, title companies, contractors.",
      tips: "Pro plan feature. Build your trusted vendor list with ratings and notes.",
    },
    vendorDirectory: {
      path: "vendor-directory",
      keywords: ["vendor directory", "find vendor", "service provider"],
      description: "Browse and manage your directory of trusted service providers and vendors.",
      tips: "Organize by category — lenders, inspectors, photographers, stagers, and more.",
    },
    teamHub: {
      path: "team-collaboration",
      keywords: ["team", "team management", "collaboration", "team hub"],
      description: "Collaborate with your team — shared clients, notes, tasks, and communication.",
      tips: "Pro plan feature. Great for teams that share leads and co-list properties.",
    },
    dealAnalyzer: {
      path: "deal-analyzer",
      keywords: ["deal analyzer", "analyze deal", "deal analysis", "evaluate deal", "ai deal"],
      description: "Deal analysis — input property details and get a comprehensive investment analysis with built-in metrics.",
      tips: "Pro plan feature. Works for flips, rentals, and wholesale deals.",
    },
    reviewRequests: {
      path: "review-requests",
      keywords: ["reviews", "review request", "testimonials", "google reviews", "client reviews"],
      description: "Send automated review requests to past clients. Build your online reputation.",
      tips: "Power plan feature. Send after closing to collect 5-star reviews on Google and Zillow.",
    },
    clientAnniversaries: {
      path: "client-anniversaries",
      keywords: ["anniversaries", "home anniversary", "birthday", "client birthday"],
      description: "Track and celebrate client milestones — home purchase anniversaries, birthdays, and special dates.",
      tips: "Power plan feature. Automated reminders so you never miss a chance to stay top of mind.",
    },
    connectedAccounts: {
      path: "connected-accounts",
      keywords: ["connected", "integrations", "connect gmail", "connect google", "api", "connected accounts"],
      description: "Manage all your integrations — Gmail, Google Calendar, Google Contacts, Walk Score, and more.",
      tips: "Connect your Google account here to enable email, calendar, and contacts sync.",
    },
    googleContacts: {
      path: "google-contacts",
      keywords: ["google contacts", "import contacts", "sync contacts"],
      description: "Two-way sync between your Google Contacts and the CRM. Import, export, and keep everything in sync.",
      tips: "Requires Google account connection. Great for importing your existing contact database.",
    },
    clientImport: {
      path: "client-import",
      keywords: ["import", "csv import", "upload contacts", "bulk import", "import clients"],
      description: "Import clients from CSV files. Map columns, preview data, and import in bulk.",
      tips: "Supports any CSV format. The system will help you map your columns to CRM fields.",
    },
    library: {
      path: "library",
      keywords: ["library", "resources", "guides", "documents library"],
      description: "Resource library with guides, templates, scripts, and educational materials.",
      tips: "A growing collection of resources to help you close more deals.",
    },
    learning: {
      path: "learning",
      keywords: ["learning", "courses", "training", "education", "learn"],
      description: "Learning center with courses on real estate topics — appraisals, fair housing, offer strategies, and more.",
      tips: "New courses added regularly. Great for new agents or anyone wanting to level up.",
    },
    settings: {
      path: "settings",
      keywords: ["settings", "preferences", "account", "profile", "password", "notification settings"],
      description: "Manage your account settings — profile, notifications, phone number setup, and preferences.",
      tips: "Set up your phone number here for texting and calling features.",
    },
    helpCenter: {
      path: "help-center",
      keywords: ["help", "support", "ticket", "bug report", "feature request", "help center"],
      description: "Get help, submit support tickets, report bugs, and request new features.",
      tips: "Start here — I (the in-app AI assistant) can usually answer right away. For anything I can't, every ticket goes to our US-based team who use Stacked daily; we aim to reply within hours on weekdays.",
    },
    pricing: {
      path: "pricing",
      keywords: ["pricing", "plans", "upgrade", "subscription", "cost", "price", "billing"],
      description: "View and manage your subscription plan — Starter, Power, and Pro tiers available.",
      tips: "Each tier unlocks more features. Upgrade anytime to access advanced tools.",
    },
    // Wholesale-specific
    sellers: {
      path: "sellers",
      keywords: ["sellers", "motivated sellers", "distressed", "seller leads"],
      crmType: "wholesaler",
      description: "Manage your seller leads — motivated sellers, distressed properties, and potential deals.",
    },
    buyers: {
      path: "buyers",
      keywords: ["cash buyers", "investor buyers", "buyer list", "wholesale buyers"],
      crmType: "wholesaler",
      description: "Manage your cash buyer list — preferences, proof of funds, and deal history.",
    },
    wholesaleDeals: {
      path: "deals",
      keywords: ["wholesale deals", "assignments", "contracts"],
      crmType: "wholesaler",
      description: "Track all your wholesale deals from lead to assignment close.",
    },
    drivingForDollars: {
      path: "driving-for-dollars",
      keywords: ["driving for dollars", "d4d", "drive for dollars", "driving"],
      crmType: "wholesaler",
      description: "Log properties you find while driving. Track routes, add notes, and convert to leads.",
    },
    offerCalculator: {
      path: "offer-calculator",
      keywords: ["mao", "maximum offer", "70% rule", "offer calculator", "wholesale offer"],
      crmType: "wholesaler",
      description: "Calculate maximum allowable offers using the 70% rule, ARV, and repair estimates.",
    },
    // Probate-specific
    probateCases: {
      path: "probate-cases",
      keywords: ["probate", "estate", "cases", "probate case", "deceased"],
      crmType: "probate",
      description: "Manage probate cases — track estates, heirs, court dates, and property status.",
    },
    probateHeirs: {
      path: "probate-heirs",
      keywords: ["heirs", "personal representative", "beneficiary", "heir finder"],
      crmType: "probate",
      description: "Track and manage heirs and personal representatives for each probate case.",
    },
    probateCourtCalendar: {
      path: "probate-court-calendar",
      keywords: ["court", "court date", "hearing", "probate court"],
      crmType: "probate",
      description: "Track all court dates, hearings, and filing deadlines across your probate cases.",
    },
    // Flipper-specific
    flipperProjects: {
      path: "flipper-projects",
      keywords: ["flip", "project", "renovation", "rehab", "flip project"],
      crmType: "flipper",
      description: "Manage your flip projects — budget, timeline, contractors, and profit tracking.",
    },
    flipperContractors: {
      path: "flipper-contractors",
      keywords: ["contractor", "contractors", "subcontractor", "plumber", "electrician"],
      crmType: "flipper",
      description: "Manage your contractor database — contact info, specialties, ratings, and bid history.",
    },
    flipperBudget: {
      path: "flipper-budget",
      keywords: ["budget", "rehab budget", "renovation cost", "flip budget"],
      crmType: "flipper",
      description: "Track rehab budgets with line-item detail — materials, labor, permits, and contingency.",
    },
    flipperARV: {
      path: "flipper-arv-calculator",
      keywords: ["arv", "after repair value", "comps", "comparable sales"],
      crmType: "flipper",
      description: "Calculate the After Repair Value using comparable sales and adjustments.",
    },
    // Addons
    socialMediaSuite: {
      path: "addon/social-media-suite",
      keywords: ["social media", "facebook", "instagram", "tiktok", "social posts"],
      description: "Complete social media management — schedule posts, manage multiple platforms, content calendar.",
      tips: "Available as an add-on. Create, schedule, and analyze social media content all from the CRM.",
    },
    cmaBuilder: {
      path: "addon/cma-builder",
      keywords: ["cma", "comparative market analysis", "market analysis", "comps report"],
      description: "Build professional Comparative Market Analysis reports for your listing presentations.",
      tips: "Available as an add-on. Impress sellers with polished, branded CMA reports.",
    },
    clientPortal: {
      path: "addon/client-portal",
      keywords: ["client portal", "portal", "client access", "client login"],
      description: "Give your clients their own portal to track their transaction progress, documents, and updates.",
      tips: "Available as an add-on. Clients love the transparency and you'll get fewer 'what's the status?' calls.",
    },
  },

  // General knowledge about the CRM
  general: {
    whatIsStacked: "Stacked is an all-in-one real estate CRM platform built for agents, wholesalers, probate specialists, and fix-and-flip investors. It combines contact management, transaction tracking, marketing tools, and specialized workflows into a single platform — so you don't need 5 different subscriptions.",
    crmTypes: "We offer four specialized CRM types: Real Estate Agent (residential sales), Wholesaler (wholesale deals & assignments), Probate Specialist (estate/probate cases), and Fix & Flip (renovation project management). Each has its own dashboard, tools, and workflows.",
    planTiers: "We have three plan tiers — Starter (essential tools to get going), Power (advanced automation, drip campaigns, e-signatures), and Pro (full analytics, team management, Deal Analyzer, neighborhood farming). All plans include core features like contacts, calendar, email, and marketing tools.",
    integrations: "We integrate with Gmail, Google Calendar, Google Contacts, Walk Score, SchoolDigger, and more. Connect everything from the Connected Accounts page in your settings.",
    gettingStarted: "To get started: 1) Add your first clients from the Clients page, 2) Connect your Gmail and Google Calendar from Connected Accounts, 3) Add your properties, and 4) Explore the Marketing Tools for ready-to-use templates.",
    importContacts: "You can import contacts three ways: CSV upload from the Client Import page, Google Contacts sync from the Google Contacts page, or manually add them one by one from the Clients page.",
    mobileApp: "Stacked is a Progressive Web App — install it from your phone's browser (Add to Home Screen on iOS, Install App on Android) for an app-like experience, offline-friendly caching, and home-screen icon. No app store required.",
    support: "Need help? Visit the Help Center to browse guides, submit support tickets, or report bugs. We respond quickly!",
    weather: "Weather is integrated into your dashboard and showing scheduler. See current conditions and forecasts right where you need them — especially useful before showings.",
    walkScore: "Property pages show Walk Score, Transit Score, Bike Score, and nearby schools automatically. Great for helping buyers understand a neighborhood.",
  },

  // Quick action suggestions by context
  quickActions: {
    newUser: [
      { label: "Add your first client", path: "clients" },
      { label: "Connect Gmail", path: "connected-accounts" },
      { label: "Explore Marketing Tools", path: "marketing-tools" },
      { label: "View your Dashboard", path: "dashboard" },
    ],
    agent: [
      { label: "View Pipeline", path: "pipeline" },
      { label: "Schedule a Showing", path: "showing-scheduler" },
      { label: "Check Closing Countdown", path: "closing-countdown" },
      { label: "Commission Tracker", path: "commission-tracker" },
    ],
    wholesaler: [
      { label: "Add a Seller", path: "sellers" },
      { label: "Offer Calculator", path: "offer-calculator" },
      { label: "Buyer List", path: "buyers" },
      { label: "Drive for Dollars", path: "driving-for-dollars" },
    ],
  },
};

// ─────────────────────────────────────────────────
// RESPONSE ENGINE — Processes questions locally
// ─────────────────────────────────────────────────

function findBestMatch(input) {
  const q = input.toLowerCase().trim();
  const words = q.split(/\s+/);

  // Score each page by keyword matches
  let bestMatch = null;
  let bestScore = 0;

  for (const [key, page] of Object.entries(KNOWLEDGE.pages)) {
    let score = 0;
    for (const keyword of page.keywords || []) {
      const kw = keyword.toLowerCase();
      if (q.includes(kw)) {
        score += kw.split(/\s+/).length * 3; // Multi-word matches score higher
      }
      for (const word of words) {
        if (kw.includes(word) && word.length > 2) score += 1;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = { key, ...page };
    }
  }

  return bestScore >= 2 ? bestMatch : null;
}

function generateResponse(input, crmSlug) {
  // Local-only fast responses for greetings and simple navigation
  const q = input.toLowerCase().trim();

  if (/^(hi|hello|hey|sup|yo|howdy|good morning|good afternoon|good evening)\b/.test(q)) {
    return {
      text: "Hey! I'm your Stacked assistant — powered by AI. I can answer questions about any feature, walk you through workflows, help troubleshoot issues, or navigate you to the right page. What can I help with?",
      suggestions: ["How do I get started?", "What can you do?", "Show me my dashboard"],
    };
  }

  // Navigation shortcuts — if user clearly wants to go somewhere, handle instantly
  if (/^(go to|take me to|open|navigate to|show me) /i.test(q)) {
    const match = findBestMatch(q);
    if (match) {
      return {
        text: `Taking you there! ${match.description}`,
        navigateTo: match.path,
        suggestions: ["What else can I help with?"],
      };
    }
  }

  // For everything else, return null — caller will use AI
  return null;
}

/**
 * Call backend chat proxy for intelligent AI responses
 */
async function getAIResponse(userMessage, conversationHistory, crmSlug, currentPath) {
  try {
    // Get auth token
    const token = (() => {
      try {
        return localStorage.getItem("__admin_token__") || localStorage.getItem("__login_user_token__") || "";
      } catch { return ""; }
    })();

    const API_BASE = import.meta.env.VITE_API_URL || "https://stacked-backend-gv43.onrender.com/api/v1";

    const response = await fetch(`${API_BASE}/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        message: userMessage,
        history: conversationHistory.slice(-10).map(m => ({ role: m.role, text: m.text })),
        context: { crmSlug, currentPath },
      }),
    });

    if (!response.ok) {
      console.warn("[Chatbot] Backend response not ok:", response.status);
      // 503 = AI intentionally not configured/available. Be honest
      // instead of silently answering with a keyword match that
      // pretends to be AI — trust matters more than the illusion.
      if (response.status === 503) {
        // Demo tours are excluded from live AI (it costs money per
        // message) — say that plainly instead of "temporarily".
        let code = null;
        try { code = (await response.json())?.code || null; } catch { /* no body */ }
        return {
          text: code === "DEMO_AI_UNAVAILABLE"
            ? "Live AI answers are included with paid plans, so they're not part of the demo tour. I can still take you to any page or explain any feature — try \"go to pipeline\" or ask about a specific tool."
            : "Live AI answers are temporarily unavailable. I can still take you to any page or explain any feature — try \"go to pipeline\" or ask about a specific tool.",
          navigateTo: null,
          suggestions: ["What can you do?", "Go to dashboard", "Explain lead scoring"],
        };
      }
      return null;
    }

    const data = await response.json();
    if (!data.success || !data.data?.text) return null;

    // Generate contextual suggestions
    const text = data.data.text;
    const suggestions = [];
    if (data.data.navigateTo) suggestions.push("What else can I help with?");
    if (/speed.to.lead/i.test(text)) suggestions.push("Set up Speed-to-Lead");
    if (/lead.scor/i.test(text)) suggestions.push("View Lead Scoring");
    if (/drip|campaign/i.test(text)) suggestions.push("Create a Drip Campaign");
    if (/bulk.send/i.test(text)) suggestions.push("Open Bulk Send");
    if (suggestions.length === 0) suggestions.push("Tell me more", "What else can I do?");

    return {
      text: data.data.text,
      navigateTo: data.data.navigateTo || null,
      suggestions: suggestions.slice(0, 3),
    };
  } catch (err) {
    console.error("[Chatbot] AI call failed:", err);
    return null;
  }
}


// ─────────────────────────────────────────────────
// CHATBOT COMPONENT
// ─────────────────────────────────────────────────

const INITIAL_MESSAGES = [
  {
    role: "bot",
    text: "Hey! I'm your Stacked assistant. Ask me anything about the CRM — I can explain features, help you find tools, or navigate you to the right page instantly.",
    suggestions: ["How do I get started?", "What can you do?", "Show me my dashboard"],
  },
];

export default function GlobalChatbot() {
  // ── ALL HOOKS MUST RUN BEFORE ANY EARLY RETURN ──
  // Rules of Hooks: the same number of hooks must fire on every
  // render. The prior version did `if (!isLoggedIn) return null`
  // AFTER 10 hooks but BEFORE 3 more (useCallback + 2 useEffect)
  // — when the login state flipped mid-flow (demo launch, auth
  // refresh, logout), React saw a different hook count on
  // consecutive renders and threw #310, which the ErrorBoundary
  // caught as a full-page "Something went wrong" lockout.
  // Every hook now runs unconditionally; we conditionally render
  // null at the bottom.
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Login-state probe. Reads localStorage on every render; cheap.
  // Same key ProtectedRoute reads.
  const isLoggedIn = (() => {
    try { return !!localStorage.getItem("__login_user_token__") || !!localStorage.getItem("__admin_token__"); }
    catch { return false; }
  })();

  // Detect CRM slug from URL. These MUST be the app's actual route
  // slugs (App.jsx mounts CRMs at /realestate, /wholesale, /probate,
  // /flipper) — a previous version validated against marketing-page
  // slugs ("real-estate", "wholesaler"), so every navigation the
  // assistant offered from inside a CRM built a dead URL like
  // /real-estate/clients (a 404).
  const crmSlug = (() => {
    const seg = location.pathname.split("/")[1];
    const valid = ["realestate", "wholesale", "probate", "flipper"];
    return valid.includes(seg) ? seg : "realestate";
  })();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const handleNavigate = (path) => {
    const fullPath = `/${crmSlug}/${path}`;
    navigate(fullPath);
  };

  const handleSend = async (text) => {
    const msg = text || input.trim();
    if (!msg) return;

    // Add user message
    const userMsg = { role: "user", text: msg };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);

    // Try local fast response first (greetings, direct navigation)
    const localResponse = generateResponse(msg, crmSlug);

    if (localResponse) {
      if (localResponse.navigateTo) handleNavigate(localResponse.navigateTo);

      const botMsg = {
        role: "bot",
        text: localResponse.text,
        suggestions: localResponse.suggestions,
        navigateTo: localResponse.offerNavigate || null,
      };
      setMessages(prev => [...prev, botMsg]);
      setIsTyping(false);
      return;
    }

    // No local match — call Claude AI
    try {
      const currentMessages = [...messages, userMsg];
      const aiResponse = await getAIResponse(msg, currentMessages, crmSlug, location.pathname);

      if (aiResponse) {
        if (aiResponse.navigateTo) handleNavigate(aiResponse.navigateTo);

        setMessages(prev => [...prev, {
          role: "bot",
          text: aiResponse.text,
          suggestions: aiResponse.suggestions,
          navigateTo: null,
        }]);
      } else {
        // AI failed — use keyword fallback
        const match = findBestMatch(msg);
        const fallbackText = match
          ? match.description + (match.tips ? `\n\n💡 ${match.tips}` : "")
          : "I couldn't process that right now. Try asking about a specific feature like 'How do drip campaigns work?' or 'Where do I track commissions?'";

        setMessages(prev => [...prev, {
          role: "bot",
          text: fallbackText,
          suggestions: match ? [`Go to ${match.keywords[0]}`, "What else can I do?"] : ["What can you do?", "How do I get started?"],
          navigateTo: match?.path || null,
        }]);
      }
    } catch (err) {
      console.error("[Chatbot] Error:", err);
      setMessages(prev => [...prev, {
        role: "bot",
        text: "Something went wrong. Try again or ask me a different question!",
        suggestions: ["What can you do?", "How do I get started?"],
      }]);
    }

    setIsTyping(false);
  };

  const handleSuggestionClick = (suggestion) => {
    // Check if it's a navigation suggestion
    if (suggestion.startsWith("Go to ") || suggestion.startsWith("Open ") || suggestion.startsWith("Take me")) {
      const target = suggestion.replace(/^(Go to |Open |Take me to )/, "").toLowerCase();
      const match = findBestMatch(target);
      if (match) {
        handleNavigate(match.path);
        setMessages(prev => [...prev,
          { role: "user", text: suggestion },
          { role: "bot", text: `Navigating you to ${match.keywords[0]}! ✨`, suggestions: ["What else can I help with?"] }
        ]);
        return;
      }
    }
    handleSend(suggestion);
  };

  // Conditional render — NOT a conditional return between hooks.
  // The chatbot is logged-in-only; hide for everyone else.
  if (!isLoggedIn) return null;

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => { setIsOpen(true); setUnread(0); }}
          className="fixed bottom-5 right-5 z-50 w-14 h-14 bg-[#1ec8c8] text-white rounded-full shadow-md flex items-center justify-center hover:scale-105 active:scale-95 transition-all duration-200"
          aria-label="Open assistant"
        >
          <MessageCircle className="w-6 h-6" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed z-50 bg-white rounded-2xl shadow-2xl shadow-black/15 border border-gray-200 flex flex-col overflow-hidden transition-all duration-300 ${
            isExpanded
              ? "bottom-0 right-0 left-0 top-0 sm:bottom-4 sm:right-4 sm:left-auto sm:top-auto sm:w-[500px] sm:h-[680px] sm:rounded-2xl"
              : "bottom-0 right-0 left-0 sm:bottom-5 sm:right-5 sm:left-auto sm:w-[370px] h-[100dvh] sm:h-[540px] sm:rounded-2xl"
          }`}
          style={{ maxHeight: "calc(100vh - 40px)" }}
        >
          {/* Header */}
          <div className="bg-[#1ec8c8] px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">Stacked Assistant</p>
                <p className="text-white/70 text-[10px]">Always here to help</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setIsExpanded(!isExpanded)} className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                {isExpanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => setIsOpen(false)} className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50/50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] ${msg.role === "user" ? "" : ""}`}>
                  {msg.role === "bot" && (
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 bg-[#1ec8c8]/10 rounded-md flex items-center justify-center shrink-0 mt-0.5">
                        <Bot className="w-3.5 h-3.5 text-[#1ec8c8]" />
                      </div>
                      <div>
                        <div className="bg-white border border-gray-100 rounded-xl rounded-tl-sm px-3.5 py-2.5 shadow-sm">
                          <p className="text-[13px] text-gray-700 leading-relaxed whitespace-pre-line">{msg.text}</p>
                        </div>

                        {/* Navigation offer */}
                        {msg.navigateTo && (
                          <button
                            onClick={() => handleNavigate(msg.navigateTo)}
                            className="mt-1.5 flex items-center gap-1.5 px-3 py-1.5 bg-[#1ec8c8]/10 text-[#1ec8c8] rounded-lg text-xs font-medium hover:bg-[#1ec8c8]/20 transition-colors"
                          >
                            <ArrowRight className="w-3 h-3" /> Go there now
                          </button>
                        )}

                        {/* Suggestions */}
                        {msg.suggestions && i === messages.length - 1 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {msg.suggestions.map((s, j) => (
                              <button
                                key={j}
                                onClick={() => handleSuggestionClick(s)}
                                className="px-2.5 py-1 bg-white border border-gray-200 text-gray-600 text-[11px] rounded-full hover:bg-[#1ec8c8]/5 hover:border-[#1ec8c8]/30 hover:text-[#1ec8c8] transition-all"
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {msg.role === "user" && (
                    <div className="bg-[#1ec8c8] text-white rounded-xl rounded-tr-sm px-3.5 py-2.5 shadow-sm">
                      <p className="text-[13px] leading-relaxed">{msg.text}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 bg-[#1ec8c8]/10 rounded-md flex items-center justify-center shrink-0">
                  <Bot className="w-3.5 h-3.5 text-[#1ec8c8]" />
                </div>
                <div className="bg-white border border-gray-100 rounded-xl rounded-tl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-gray-100 bg-white shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Ask me anything..."
                className="flex-1 px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#1ec8c8] focus:ring-1 focus:ring-[#1ec8c8]/20 transition-colors"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim()}
                className="p-2.5 bg-[#1ec8c8] text-white rounded-xl hover:bg-[#17a5a5] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[9px] text-gray-300 text-center mt-1.5">Powered by Stacked AI</p>
          </div>
        </div>
      )}
    </>
  );
}
